import { useState, useEffect } from "react";
import { Search, ChevronLeft, ChevronRight, CheckCircle2, XCircle, Clock } from "lucide-react";
import { Dropdown } from "./Dropdown";
import { CodeBlock } from "./CodeBlock";
import type { ControlExecutionStatusResponse } from "../../api/frameworksApi";

function normStatus(s?: string): "passed" | "failed" | "pending" {
  const v = (s ?? "").toLowerCase();
  if (v.includes("pass")) return "passed";
  if (v.includes("fail")) return "failed";
  if (v.includes("pending")) return "pending";
  return "pending";
}

const OPA_PER_PAGE = 3;

/**
 * Shared Automation Results checks view used by the execution and review
 * automation popups. Renders the toolbar (search · pass/fail filter · rows per
 * page), the per-check cards (frozen OPA snapshot with Command + Evidence), a
 * legacy OPA-results fallback, and the empty state. Owns its own filter / search
 * / rows / page state. Page-specific chrome (header, follow-up, action buttons)
 * stays in each page around this component.
 */
export function AutomationChecksView({ control }: { control: ControlExecutionStatusResponse }) {
  const [filter, setFilter] = useState<"all" | "passed" | "failed" | "pending">("all");
  const [search, setSearch] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [page, setPage] = useState(0);
  useEffect(() => {
    setPage(0);
    setFilter("all");
    setSearch("");
  }, [control.planControlId]);

  return (
    <>
      {/* ── Per-check view: toolbar (search · filter · rows) + cards ── */}
      {(control.executionViewStatements ?? []).some((s) => (s.assets ?? []).length > 0) && (() => {
        const assetStmts = (control.executionViewStatements ?? []).filter(
          (stmt) => (stmt.assets ?? []).length > 0,
        );
        const flatChecks = assetStmts.flatMap((stmt) =>
          (stmt.assets ?? []).flatMap((asset) =>
            (asset.controls ?? []).map((tech) => ({ stmt, asset, tech })),
          ),
        );
        const q = search.trim().toLowerCase();
        const filteredChecks = flatChecks.filter(({ stmt, asset, tech }) => {
          const status = normStatus(tech.status);
          if (filter !== "all" && status !== filter) return false;
          if (!q) return true;
          const haystack = [
            tech.tech_control_id,
            tech.tech_control_description,
            tech.status,
            tech.command,
            tech.evidence_data,
            stmt.statement_label,
            stmt.statement_description,
            asset.asset_hostname,
            asset.component_name,
            asset.ip_address,
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
          return haystack.includes(q);
        });
        const totalCheckPages = Math.max(1, Math.ceil(filteredChecks.length / rowsPerPage));
        const safePage = Math.min(page, totalCheckPages - 1);
        const pagedChecks = filteredChecks.slice(safePage * rowsPerPage, (safePage + 1) * rowsPerPage);
        const filterTabs: { key: typeof filter; label: string }[] = [
          { key: "all", label: "All" },
          { key: "passed", label: "Passed" },
          { key: "failed", label: "Failed" },
        ];
        return (
          <div className="space-y-3">
            {/* Toolbar — sticky search/filter/page-size bar. Stuck 20px above
                the scrollport top (negative top + matching extra pt) so the
                opaque background fully covers the parent modal body's
                padding-top area; otherwise scrolled content (statement
                badges, etc.) bleeds through above the bar. Same fix
                applied in AssessmentResultDetail.tsx. */}
            <div
              className="sticky -top-[20px] z-50 -mx-5 px-5 pt-[24px] pb-3 bg-white dark:bg-[#131320] border-b border-[#e2e8f0] dark:border-white/[0.06] flex flex-wrap items-center gap-2 [isolation:isolate] shadow-[0_6px_10px_-6px_rgba(0,0,0,0.35)] dark:shadow-[0_6px_10px_-6px_rgba(0,0,0,0.7)]"
            >
              <div className="relative flex-1 min-w-[150px] max-w-[280px]">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-[#64748b] dark:text-white/60" />
                <input
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                  placeholder="Search checks, hosts, descriptions"
                  className="w-full h-9 pl-8 pr-3 text-[12px] rounded-lg bg-[#f1f5f9] dark:bg-white/[0.05] border border-[#e2e8f0] dark:border-white/[0.08] outline-none focus:border-indigo-500 dark:focus:border-violet-500 text-[#0f172a] dark:text-white placeholder:text-[#64748b] dark:placeholder:text-white/50"
                />
              </div>
              <div className="flex items-center h-9 gap-0.5 bg-[#f1f5f9] dark:bg-white/[0.05] rounded-lg px-1 border border-[#e2e8f0] dark:border-white/[0.08]">
                {filterTabs.map((t) => (
                  <button
                    key={t.key}
                    onClick={() => { setFilter(t.key); setPage(0); }}
                    className={`px-2.5 h-7 rounded-md text-[12px] font-bold transition-all ${
                      filter === t.key
                        ? "bg-indigo-600 dark:bg-violet-600 text-white shadow-sm"
                        : "text-[#475569] dark:text-white/70 hover:text-[#0f172a] dark:hover:text-white"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2 ml-auto pl-2">
                <span className="text-[13px] font-semibold text-[#64748b] dark:text-white/60 whitespace-nowrap">Rows per page</span>
                <Dropdown
                  value={String(rowsPerPage)}
                  onChange={(v) => { setRowsPerPage(Number(v)); setPage(0); }}
                  options={[10, 25, 50, 100].map((n) => ({ value: String(n), label: String(n) }))}
                  size="md"
                  align="end"
                  className="!min-w-[88px]"
                />
              </div>
            </div>

            {pagedChecks.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-[13px] font-semibold text-[#475569] dark:text-white/70">
                  No checks match your filters
                </p>
                <p className="text-[12px] text-[#64748b] dark:text-white/50 mt-1">
                  Try a different status or clear the search.
                </p>
              </div>
            ) : pagedChecks.map(({ stmt, asset, tech }, idx) => {
              const status = normStatus(tech.status);
              const passed = status === "passed";
              const pending = status === "pending";
              const st = (stmt.state ?? "NEW").toUpperCase();
              const stBadge =
                st === "AWAITING_REVIEW"
                  ? { label: "Awaiting Reviewer", color: "bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-500/20" }
                  : st === "FOLLOWUP_RESOLVED"
                    ? { label: "Awaiting Re-Review", color: "bg-sky-50 dark:bg-sky-500/10 text-sky-700 dark:text-sky-300 border-sky-200 dark:border-sky-500/20" }
                    : st === "REVIEWED"
                      ? { label: "Reviewed", color: "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-500/20" }
                      : st === "FOLLOWUP"
                        ? { label: "Follow-Up Required", color: "bg-orange-50 dark:bg-orange-500/10 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-500/20" }
                        : null;
              return (
                <div
                  key={`${stmt.statement_id}-${asset.asset_uuid}-${idx}`}
                  className="rounded-xl border border-[#e2e8f0] dark:border-white/[0.08] bg-white dark:bg-white/[0.03] p-4 space-y-2.5"
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    {tech.tech_control_id && (
                      <span className="text-[12.5px] font-mono font-bold text-indigo-700 dark:text-indigo-100 bg-indigo-50 dark:bg-indigo-500/15 border border-indigo-200/60 dark:border-indigo-500/20 px-2.5 py-1 rounded-md">
                        <span className="font-sans font-semibold text-indigo-500 dark:text-indigo-300">Control: </span>
                        {tech.tech_control_id?.toUpperCase()}
                      </span>
                    )}
                    {asset.asset_hostname && (
                      <span className="text-[12.5px] font-semibold text-sky-800 dark:text-sky-100 bg-sky-50 dark:bg-sky-500/15 border border-sky-200/60 dark:border-sky-500/20 px-2.5 py-1 rounded-md">
                        <span className="font-semibold text-sky-600 dark:text-sky-300">Hostname: </span>
                        {asset.asset_hostname}
                      </span>
                    )}
                    {asset.component_name && (
                      <span className="text-[12.5px] font-semibold text-orange-800 dark:text-orange-100 bg-orange-50 dark:bg-orange-500/15 border border-orange-200/60 dark:border-orange-500/20 px-2.5 py-1 rounded-md">
                        <span className="font-semibold text-orange-600 dark:text-orange-300">Component: </span>
                        {asset.component_name}
                      </span>
                    )}
                    <span
                      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[12.5px] font-bold ${
                        passed
                          ? "bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-100"
                          : pending
                            ? "bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-100"
                            : "bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-100"
                      }`}
                    >
                      <span className="font-semibold opacity-80">Status: </span>
                      {passed ? (
                        <CheckCircle2 className="size-3.5" />
                      ) : pending ? (
                        <Clock className="size-3.5" />
                      ) : (
                        <XCircle className="size-3.5" />
                      )}
                      {tech.status}
                    </span>
                    {stBadge && (
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[12.5px] font-bold border ${stBadge.color}`}>
                        <span className="font-semibold opacity-80">Review: </span>
                        {stBadge.label}
                      </span>
                    )}
                  </div>
                  {(asset.ip_address || asset.scanned_at) && (
                    <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-lg bg-[#f1f5f9] dark:bg-white/[0.05] border border-[#e2e8f0] dark:border-white/[0.08] text-[12.5px] text-[#475569] dark:text-white/75">
                      <Clock className="size-3.5 text-[#94a3b8] dark:text-white/50 flex-shrink-0" />
                      <span>
                        {asset.ip_address && <>Data from <span className="font-mono font-bold text-[#1e293b] dark:text-white/95">{asset.ip_address}</span></>}
                        {asset.ip_address && asset.scanned_at && " · "}
                        {asset.scanned_at && <>scanned at <span className="font-semibold text-[#1e293b] dark:text-white/90">{new Date(asset.scanned_at).toLocaleString("en-GB")}</span></>}
                      </span>
                    </div>
                  )}
                  {tech.tech_control_description && (
                    <p className="text-[12px] text-[#334155] dark:text-white/80 leading-relaxed">
                      {tech.tech_control_description}
                    </p>
                  )}
                  {tech.command && (
                    <CodeBlock label="Command Executed" content={tech.command} status={status} />
                  )}
                  {tech.evidence_data && (
                    <CodeBlock label="Evidence" content={tech.evidence_data} status={status} />
                  )}
                </div>
              );
            })}

            {filteredChecks.length > 0 && (
              <div className="flex items-center justify-between pt-2">
                <span className="text-[12px] text-[#475569] dark:text-white/65">
                  Showing{" "}
                  <span className="font-bold text-[#0f172a] dark:text-white/90">
                    {safePage * rowsPerPage + 1}–{Math.min((safePage + 1) * rowsPerPage, filteredChecks.length)}
                  </span>{" "}
                  of <span className="font-bold text-[#0f172a] dark:text-white/90">{filteredChecks.length}</span> checks
                </span>
                {totalCheckPages > 1 && (
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => setPage(Math.max(0, safePage - 1))}
                      disabled={safePage === 0}
                      className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-[12px] font-bold text-[#475569] dark:text-white/70 bg-[#f1f5f9] dark:bg-white/[0.05] hover:bg-[#e2e8f0] dark:hover:bg-white/[0.08] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronLeft className="size-3.5" /> Previous
                    </button>
                    <span className="text-[12px] font-semibold text-[#475569] dark:text-white/65 px-1">
                      Page {safePage + 1} of {totalCheckPages}
                    </span>
                    <button
                      onClick={() => setPage(Math.min(totalCheckPages - 1, safePage + 1))}
                      disabled={safePage >= totalCheckPages - 1}
                      className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-[12px] font-bold text-[#475569] dark:text-white/70 bg-[#f1f5f9] dark:bg-white/[0.05] hover:bg-[#e2e8f0] dark:hover:bg-white/[0.08] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      Next <ChevronRight className="size-3.5" />
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {/* OPA Results — legacy fallback when execution-view has no snapshot */}
      {(control.executionViewAssets ?? []).length === 0 && (() => {
        const allOpa = (control.opaResults ?? []).filter(
          (opa, i, arr) => arr.findIndex((o) => o.opaControlId === opa.opaControlId) === i,
        );
        const totalOpaPages = Math.ceil(allOpa.length / OPA_PER_PAGE);
        const pagedOpa = allOpa.slice(page * OPA_PER_PAGE, (page + 1) * OPA_PER_PAGE);
        return (
          <>
            {pagedOpa.map((opa, sliceIdx) => {
              const opaIdx = page * OPA_PER_PAGE + sliceIdx;
              const passed = opa.controlStatus === "passed";
              return (
                <div
                  key={`${opa.opaControlId}-${opaIdx}`}
                  className={`rounded-xl border p-4 space-y-2 ${
                    passed
                      ? "border-emerald-100 dark:border-emerald-500/10 bg-emerald-50/30 dark:bg-emerald-500/[0.03]"
                      : "border-red-100 dark:border-red-500/10 bg-red-50/30 dark:bg-red-500/[0.03]"
                  }`}
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[12px] font-bold ${
                        passed
                          ? "bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-400"
                          : "bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-400"
                      }`}
                    >
                      {passed ? <CheckCircle2 className="size-3" /> : <XCircle className="size-3" />}
                      {passed ? "Passed" : "Failed"}
                    </span>
                    {opa.catalogName && (
                      <span className="text-[12px] text-[#64748b] dark:text-white/40 bg-[#f1f5f9] dark:bg-white/[0.04] px-2 py-0.5 rounded">
                        {opa.catalogName}
                      </span>
                    )}
                  </div>
                  {opa.assetName && (
                    <div className="flex items-center gap-2 text-[12px] text-[#475569] dark:text-white/50">
                      <span className="font-medium text-[#64748b] dark:text-white/40">Asset:</span>
                      <span className="font-mono text-[#0f172a] dark:text-white/70">{opa.assetName}</span>
                      {opa.assetIdentifierType && (
                        <span className="text-[#94a3b8] dark:text-white/30">({opa.assetIdentifierType})</span>
                      )}
                    </div>
                  )}
                  {(opa.evidence ?? []).length > 0 && (
                    <div className="mt-2">
                      <p className={`text-[12px] font-black tracking-wide mb-1.5 ${passed ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}`}>
                        Evidence ({opa.evidence.length})
                      </p>
                      <div className={`rounded-lg border bg-[#f8f9fa] dark:bg-[#1e1e1e] overflow-hidden ${passed ? "border-emerald-200 dark:border-emerald-500/20" : "border-red-200 dark:border-red-500/20"}`}>
                        <div className="flex items-center justify-between px-3 py-1.5 bg-[#e9ecef] dark:bg-[#2d2d2d] border-b border-[#dee2e6] dark:border-[#404040]">
                          <span className="text-[10px] text-[#6c757d] dark:text-[#858585] font-mono">evidence.json</span>
                          <span className="text-[10px] text-[#858585]">{opa.evidence.length} items</span>
                        </div>
                        <pre className="px-3 py-2.5 text-[12px] text-[#212529] dark:text-[#d4d4d4] overflow-auto max-h-[500px] font-mono leading-relaxed m-0 whitespace-pre-wrap break-words">
{JSON.stringify(opa.evidence, null, 2)}
                        </pre>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            {totalOpaPages > 1 && (
              <div className="flex items-center justify-between pt-2">
                <button
                  onClick={() => setPage(Math.max(0, page - 1))}
                  disabled={page === 0}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-[12px] font-medium text-[#475569] dark:text-white/50 bg-[#f1f5f9] dark:bg-white/[0.04] hover:bg-[#e2e8f0] dark:hover:bg-white/[0.08] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="size-3.5" /> Previous
                </button>
                <span className="text-[12px] text-[#64748b] dark:text-white/40">
                  Page {page + 1} of {totalOpaPages}
                </span>
                <button
                  onClick={() => setPage(Math.min(totalOpaPages - 1, page + 1))}
                  disabled={page >= totalOpaPages - 1}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-[12px] font-medium text-[#475569] dark:text-white/50 bg-[#f1f5f9] dark:bg-white/[0.04] hover:bg-[#e2e8f0] dark:hover:bg-white/[0.08] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Next <ChevronRight className="size-3.5" />
                </button>
              </div>
            )}
          </>
        );
      })()}

      {(control.opaResults ?? []).length === 0 &&
        (control.executionViewAssets ?? []).length === 0 && (
        <div className="text-center py-8">
          <p className="text-[13px] text-[#cbd5e1] dark:text-white/75">
            No automated checks available for this control.
          </p>
        </div>
      )}
    </>
  );
}
